/**
 * HeroSection - Project selector and status badges for the Projects page.
 * Title is handled by PageHeader in the parent component.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CloudOff, Upload, Loader2, ChevronDown } from 'lucide-react';
import type { ProjectConfig } from '@/lib/types';

interface HeroSectionProps {
  projects: ProjectConfig[];
  selectedProjectId: string | null;
  onProjectSelect: (projectId: string | null) => void;
  isCloudEnabled: boolean;
  isReady: boolean;
  isAuthenticated: boolean;
  isSyncing: boolean;
  localDocumentCount: number;
  onSyncClick: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  projects,
  selectedProjectId,
  onProjectSelect,
  isCloudEnabled,
  isReady,
  isAuthenticated,
  isSyncing,
  localDocumentCount,
  onSyncClick,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Project Selector Dropdown */}
      <div className="relative inline-block">
        <select
          value={selectedProjectId || ''}
          onChange={(e) => onProjectSelect(e.target.value || null)}
          disabled={projects.length === 0}
          className="appearance-none px-4 py-2 pr-10 text-base font-medium text-white bg-white/10 border border-white/20 rounded-xl cursor-pointer hover:border-cyan-400/40 hover:bg-white/15 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 focus:outline-none transition-colors backdrop-blur-sm disabled:cursor-default disabled:opacity-50"
        >
          <option value="" className="text-slate-900">{t('projects.allProjects')}</option>
          {[...projects].sort((a, b) => a.projectName.localeCompare(b.projectName)).map((project) => (
            <option key={project.id} value={project.id} className="text-slate-900">
              {project.projectName}
            </option>
          ))}
        </select>
        {projects.length > 0 && (
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 pointer-events-none" />
        )}
      </div>

      {/* Sync Status Badge */}
      {!isCloudEnabled && isReady && (
        <button
          onClick={onSyncClick}
          disabled={isSyncing || !isAuthenticated || localDocumentCount === 0}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] sm:text-xs font-medium transition-colors ${
            isAuthenticated && localDocumentCount > 0
              ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-400/30 cursor-pointer'
              : 'bg-white/10 text-slate-400 border border-white/10 cursor-default'
          } disabled:opacity-50`}
          title={
            !isAuthenticated
              ? t('projects.syncTitleNotAuthenticated')
              : localDocumentCount === 0
                ? t('projects.syncTitleNoLocal')
                : t('projects.syncTitleReady')
          }
        >
          {isSyncing ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {t('projects.syncing')}
            </>
          ) : (
            <>
              <CloudOff className="w-3.5 h-3.5" />
              {t('projects.localOnly')}
              {localDocumentCount > 0 && isAuthenticated && (
                <span className="ml-1 flex items-center gap-0.5">
                  <Upload className="w-3 h-3" />
                  {localDocumentCount}
                </span>
              )}
            </>
          )}
        </button>
      )}

    </div>
  );
};

export default HeroSection;
